/**
 * WCC360 v2 — Career Dossier™ model.
 *
 * This is the full written report a learner (and their parents) get at the
 * end of the walk. It is modelled on the section architecture of a
 * conventional school career-guidance report — cover, letter, how to read
 * it, the world of work, a psychometric section, a fitment section, and a
 * summary — because that is the shape parents and counsellors already know
 * how to read.
 *
 * What it is NOT is a copy of that report's CONTENT. We do not administer
 * an aptitude battery, so there is no aptitude section here: inventing
 * "Numerical: 7/10" from a preference walk would be a fabricated number
 * dressed as a measurement, and the whole point of v2 is that we don't do
 * that. Every section below is populated only from signals this assessment
 * genuinely produced:
 *
 *   Section I-A  How you work         ← archetype tally (revealed choices)
 *   Section I-B  What you need        ← 13 preference slots (symmetric)
 *   Section I-C  What you can carry   ← 7 tolerance slots (asymmetric)
 *   Section I-D  What pulls you       ← passions, childhood dream, pull picks
 *   Section II   Fitment by family    ← the live engine, over the eligible pool
 *   Section III  Summary & next steps ← ranked matches + provenance
 *
 * PURE MODULE. No React, no network, no randomness, no clock reads beyond
 * the `generatedAt` the caller passes in. Everything here is a deterministic
 * function of the assessment state, so the same walk always renders the same
 * dossier and the golden tests can pin it.
 *
 * The engine is untouched. This module only READS the exported scorers in
 * `@/lib/scoring`; it never re-weights, re-tiers or re-implements them.
 */
import {
  WEIGHTS, effectiveCapacity, entryCost,
  scoreArchetype, scoreContext, scoreDemand, scoreDomain, scoreSalary, scoreValues,
  type Archetype, type Career, type Learner, type MatchResult,
} from "@/lib/scoring";
import { ARCHETYPES, type ArchetypeVector } from "./archetypes";
import { buildConvergence, type Convergence } from "./convergence";
import type { Band } from "./band";
import {
  ARCHETYPE_TAGLINES, sentenceCase, withArticle, firstPersonFamilyStrength, formatSalary, whyItMatched,
} from "./results";
import { readApprovedObserverInput } from "./observerStore";
import { relationshipNoun, type ApprovedObserverInput } from "./observer";
import { PASSIONS } from "@/data/passions";
import { selectedPassions } from "./passions";
import { causeById } from "./causes";
import { dreamById, selectedPulls } from "./careerPull";
import { emptySlots, type SlotKey, type SlotState } from "./slots";
import type { Part1Answers } from "./types";

// =========================================================================
// Shared primitives
// =========================================================================

export type StrengthBand = "high" | "medium" | "low";

/** A 0..100 magnitude that always carries the sentence explaining it. */
export interface Explained {
  /** 0..100. Never rendered without `meaning` beside it. */
  value: number;
  /** Plain-language reading of that number, in the learner's second person. */
  meaning: string;
  /** True when two screens disagreed and we kept the conservative value. */
  lowConfidence?: boolean;
}

/** A bipolar spectrum: two named poles and a marker somewhere between. */
export interface Spectrum extends Explained {
  key: string;
  label: string;
  poleLow: string;
  poleHigh: string;
}

/** A one-directional load bar: how much of something you can carry. */
export interface LoadBar extends Explained {
  key: string;
  label: string;
  /** What a job asking for MORE than this would cost the learner. */
  ifExceeded: string;
}

// =========================================================================
// Section I-A — How you work (archetypes)
// =========================================================================

export interface ArchetypeRow {
  name: Archetype;
  /** 0..100, normalised against the strongest archetype in this walk. */
  value: number;
  band: StrengthBand;
  tagline: string;
}

/** Prose for a leading archetype — the "high potential" voice. */
const ARCHETYPE_LEAD: Record<Archetype, string> = {
  Builder:
    "You reach for the practical version of a problem. Across the walk you chose making, fixing and finishing over discussing — and you want to be able to point at the thing when it's done.",
  Thinker:
    "You stay with a problem past the point most people hand it off. You chose depth over speed repeatedly, and you're comfortable not knowing the answer yet.",
  Creator:
    "You keep choosing the blank page. Given a choice between improving something that exists and making something that doesn't, you went the second way more than once.",
  Connector:
    "You do your best work in the space between people. You chose the option that involved reading a room, bridging two sides, or getting people talking.",
  Organizer:
    "You turn mess into something that runs. Where others saw chaos in the scenarios, you reached for the structure underneath it.",
  Leader:
    "You take the call. Given the option to advise or to decide, you decided — and you accepted the consequences that came attached.",
  Caregiver:
    "You show up for people at the point it costs something. You chose the harder, closer option where a person needed you rather than the cleaner one.",
  Advocate:
    "You want the work to fix something that's wrong. You consistently picked the option that changed the rule rather than the one that worked around it.",
};

/** Prose for a supporting archetype — the "medium potential" voice. */
const ARCHETYPE_SUPPORT: Record<Archetype, string> = {
  Builder: "You'll happily get hands-on when the work needs it, without needing every day to look like that.",
  Thinker: "You go deep when the problem earns it, but you don't need every problem to be hard.",
  Creator: "There's real creative range here — it shows up when you're given room, not as a constant demand.",
  Connector: "You're good with people when it matters, and you don't need it to be the whole job.",
  Organizer: "You bring order when something needs it, without wanting to own the whole system.",
  Leader: "You'll step up when nobody else does. You just don't need the chair to feel useful.",
  Caregiver: "You look after the people around you as a matter of course, not as a job description.",
  Advocate: "You notice unfairness and you'll say something. It's a value you hold, not the engine of your week.",
};

/** Prose for a quiet archetype — the "low" voice, written without deficit. */
const ARCHETYPE_QUIET: Record<Archetype, string> = {
  Builder: "Hands-on making isn't where your energy goes. That's a preference, not a limitation.",
  Thinker: "Long solitary analysis isn't your natural pull — you'd rather move than model.",
  Creator: "Open-ended creative work isn't what you reach for. You'd rather improve something real.",
  Connector: "Constant people-brokering isn't your default. You can do it; it just costs you something.",
  Organizer: "Running the system isn't your instinct. You'd rather someone else held the spreadsheet.",
  Leader: "Being the one who decides isn't what you're after. Plenty of excellent work doesn't need it.",
  Caregiver: "Sustained caring work isn't where you're pulled. Knowing that early saves years.",
  Advocate: "Campaigning isn't your register. You'd rather do the work than argue for it.",
};

export interface HowYouWork {
  primary: Archetype;
  secondary: Archetype;
  primaryTagline: string;
  secondaryTagline: string;
  rows: ArchetypeRow[];
  /** Grouped prose, mirroring high / medium / low potential areas. */
  leading: { name: Archetype; prose: string }[];
  supporting: { name: Archetype; prose: string }[];
  quiet: { name: Archetype; prose: string }[];
  /** How many Part 2 decisions fed this. Provenance, shown in the footer. */
  decisionsCounted: number;
}

export function buildHowYouWork(
  tally: ArchetypeVector,
  primary: Archetype,
  secondary: Archetype,
): HowYouWork {
  const values = ARCHETYPES.map((a) => tally[a] ?? 0);
  const max = Math.max(...values, 1);
  const total = values.reduce((s, v) => s + v, 0);

  const rows: ArchetypeRow[] = ARCHETYPES
    .map((name) => {
      const raw = tally[name] ?? 0;
      const value = Math.round((raw / max) * 100);
      const band: StrengthBand = value >= 67 ? "high" : value >= 34 ? "medium" : "low";
      return { name, value, band, tagline: ARCHETYPE_TAGLINES[name] };
    })
    .sort((a, b) => b.value - a.value || ARCHETYPES.indexOf(a.name) - ARCHETYPES.indexOf(b.name));

  return {
    primary,
    secondary,
    primaryTagline: ARCHETYPE_TAGLINES[primary],
    secondaryTagline: ARCHETYPE_TAGLINES[secondary],
    rows,
    leading: rows.filter((r) => r.band === "high").map((r) => ({ name: r.name, prose: ARCHETYPE_LEAD[r.name] })),
    supporting: rows.filter((r) => r.band === "medium").map((r) => ({ name: r.name, prose: ARCHETYPE_SUPPORT[r.name] })),
    quiet: rows.filter((r) => r.band === "low").map((r) => ({ name: r.name, prose: ARCHETYPE_QUIET[r.name] })),
    decisionsCounted: Math.round(total),
  };
}

// =========================================================================
// Section I-B — What you need from work (preference spectra)
// =========================================================================

interface SpectrumSpec {
  key: SlotKey;
  label: string;
  poleLow: string;
  poleHigh: string;
  /** Reading at the low end, the middle, and the high end. */
  low: string;
  mid: string;
  high: string;
  group: string;
}

const SPECTRA: SpectrumSpec[] = [
  {
    key: "prefOutdoor", group: "Where the day happens",
    label: "Indoors or outdoors", poleLow: "Indoors", poleHigh: "Outdoors",
    low: "You want a roof over the work. Weather is not part of the appeal.",
    mid: "You'd take a mix — some of the week outside, most of it not.",
    high: "You want to be outside for a real part of the week, not just on the commute.",
  },
  {
    key: "prefMovement", group: "Where the day happens",
    label: "Sitting or moving", poleLow: "Mostly seated", poleHigh: "On your feet",
    low: "A seated, settled day suits you. You do your best thinking in one place.",
    mid: "You want to be able to get up, without the job being physical.",
    high: "A desk all day would wear you down. You need the work to move.",
  },
  {
    key: "prefScreenTime", group: "Where the day happens",
    label: "Screen time", poleLow: "Away from screens", poleHigh: "Screen-heavy is fine",
    low: "You'd rather the work happened off the screen. That rules more in than you'd think.",
    mid: "Screens are a tool, not the job. A normal share is fine.",
    high: "You're comfortable with a screen-led day and the depth of work that allows.",
  },
  {
    key: "prefTravel", group: "Where the day happens",
    label: "Travel", poleLow: "Home every night", poleHigh: "Travel is part of it",
    low: "You want to sleep in your own bed. Roles built on travel would grind on you.",
    mid: "Occasional travel is fine. A permanently packed bag is not.",
    high: "Travel reads as a benefit to you, not a cost.",
  },
  {
    key: "prefStableSchedule", group: "Rhythm",
    label: "Predictable hours", poleLow: "Variable is fine", poleHigh: "Predictable week",
    low: "You can take an irregular week. Shift work and seasons don't frighten you.",
    mid: "You'd like a broadly steady week with room for the odd late one.",
    high: "You need to know when your week starts and ends. That is a real requirement, not a preference.",
  },
  {
    key: "prefFocusVsSwitching", group: "Rhythm",
    label: "Focus or switching", poleLow: "Deep focus", poleHigh: "Constant switching",
    low: "You want long uninterrupted runs at one thing. Fragmented days cost you output.",
    mid: "You can hold focus and handle interruption. Neither breaks you.",
    high: "You're at your best juggling. A single task all week would flatten you.",
  },
  {
    key: "prefAutonomy", group: "Voice and ownership",
    label: "Autonomy", poleLow: "Clear direction", poleHigh: "Run your own work",
    low: "You'd rather be told the goal clearly than invent it. That's an efficient way to work.",
    mid: "You want direction on the what and freedom on the how.",
    high: "You need to own the call. Heavy oversight would be the thing that made you leave.",
  },
  {
    key: "prefSoloVsTeam", group: "Voice and ownership",
    label: "Solo or team", poleLow: "Mostly solo", poleHigh: "Mostly together",
    low: "You do your real work alone. Teams are for the edges of it.",
    mid: "You want a team you're part of, and hours in the day that are yours.",
    high: "You think out loud with other people. Isolation would slow you down.",
  },
  {
    key: "prefCreativity", group: "Voice and ownership",
    label: "Creative room", poleLow: "A proven method", poleHigh: "A blank page",
    low: "You want a method that works and permission to run it well.",
    mid: "You want a frame to work inside, with room to improve it.",
    high: "You need a blank page. Following someone else's template would bore you out of the job.",
  },
  {
    key: "prefImpactVisibility", group: "What the work is for",
    label: "Seeing the impact", poleLow: "Impact further away", poleHigh: "See who you helped",
    low: "You don't need to meet the person you helped to know the work counted.",
    mid: "You'd like to see the result, without needing it in front of you daily.",
    high: "You need to see who your work landed on. Abstract impact wouldn't hold you.",
  },
  {
    key: "prefIncomeUpside", group: "What the work is for",
    label: "How much money matters", poleLow: "Not the driver", poleHigh: "Income upside matters",
    low: "Money isn't what's pulling you. We've weighted your matches accordingly and not pushed you at high-paying work you didn't ask for.",
    mid: "You want to be paid properly without organising your life around the number.",
    high: "The income ceiling matters to you, and we've weighted the matching to respect that.",
  },
  {
    key: "prefPrestige", group: "What the work is for",
    label: "Recognition", poleLow: "Quiet work is fine", poleHigh: "Recognition matters",
    low: "You don't need the title. Quiet, respected work suits you.",
    mid: "You'd like the work recognised without needing a stage.",
    high: "Being known for the work matters to you. Say that out loud when you choose.",
  },
  {
    key: "prefStability", group: "What the work is for",
    label: "Stability", poleLow: "Comfortable with risk", poleHigh: "Stability first",
    low: "You can carry uncertainty. That opens up early-stage and self-employed routes most people can't take.",
    mid: "You want reasonable security with room to take a calculated swing.",
    high: "You want the ground to be solid. Volatile work would cost you sleep, and that's worth respecting.",
  },
];

function readSpectrum(spec: SpectrumSpec, value: number): string {
  if (value <= 33) return spec.low;
  if (value >= 67) return spec.high;
  return spec.mid;
}

export interface SpectrumGroup {
  group: string;
  items: Spectrum[];
}

export function buildWhatYouNeed(L: Learner, slots: SlotState): SpectrumGroup[] {
  const groups = new Map<string, Spectrum[]>();
  for (const spec of SPECTRA) {
    const value = Math.round((L[spec.key] as number) ?? 50);
    const item: Spectrum = {
      key: spec.key,
      label: spec.label,
      poleLow: spec.poleLow,
      poleHigh: spec.poleHigh,
      value,
      meaning: readSpectrum(spec, value),
      lowConfidence: slots.lowConfidence[spec.key] ?? false,
    };
    const arr = groups.get(spec.group) ?? [];
    arr.push(item);
    groups.set(spec.group, arr);
  }
  return [...groups.entries()].map(([group, items]) => ({ group, items }));
}

// =========================================================================
// Section I-C — What you can carry (tolerances)
// =========================================================================

interface LoadSpec {
  key: SlotKey;
  label: string;
  low: string;
  mid: string;
  high: string;
  ifExceeded: string;
}

const LOADS: LoadSpec[] = [
  {
    key: "tolPeopleContact", label: "Sustained people contact",
    low: "Long stretches of back-to-back human contact drain you faster than they refill you.",
    mid: "You can hold a normal amount of people contact through a working day.",
    high: "You can be with people all day and still have something left.",
    ifExceeded: "Roles built on constant contact would burn you out before they made you good at them.",
  },
  {
    key: "tolPublicSpeaking", label: "Speaking in front of people",
    low: "Presenting costs you real energy. That's a genuine constraint, not shyness to be trained away.",
    mid: "You can present when it's needed without it dominating your week.",
    high: "A room doesn't frighten you. That opens up client, teaching and leadership routes.",
    ifExceeded: "Jobs where the presenting IS the job would leave you dreading Mondays.",
  },
  {
    key: "tolEmotionalLoad", label: "Emotional weight",
    low: "You'd carry other people's hardest days home with you. We've kept high-distress work out of your best matches.",
    mid: "You can hold difficult situations and put them down afterwards.",
    high: "You can be near serious distress and still function. That's rarer than you think, and it's employable.",
    ifExceeded: "Work near crisis or grief would cost you more than the job pays.",
  },
  {
    key: "tolSocialLoad", label: "Social intensity",
    low: "A loud, high-contact environment would leave you empty by Wednesday.",
    mid: "A normal social environment sits comfortably with you.",
    high: "Busy, noisy, people-dense environments energise rather than deplete you.",
    ifExceeded: "Open, high-churn environments would take more from you than the work gives back.",
  },
  {
    key: "tolClosePhysical", label: "Close physical work",
    low: "Work that puts you physically close to other people's bodies isn't for you.",
    mid: "You're fine with a normal amount of physical proximity.",
    high: "Close physical work doesn't bother you — that's a real requirement in care and trades.",
    ifExceeded: "Hands-on care and treatment roles would be uncomfortable in a way that doesn't fade.",
  },
  {
    key: "tolSensory", label: "Sensory exposure",
    low: "Noise, smell, mess and hazard are things you'd rather the job didn't include.",
    mid: "You can take a normal amount of noise and mess.",
    high: "You can work through noise, smell and mess without it registering as a cost.",
    ifExceeded: "Sites, wards and kitchens would wear on you day after day.",
  },
  {
    key: "tolGruntWork", label: "Repetitive and admin work",
    low: "Repetitive process work would flatten you fast.",
    mid: "You'll do your share of the boring part without resenting it.",
    high: "You can absorb the unglamorous half of a job, which is most of the reason people get good at one.",
    ifExceeded: "Roles that are mostly process would have you looking elsewhere inside a year.",
  },
];

function readLoad(spec: LoadSpec, value: number): string {
  if (value <= 33) return spec.low;
  if (value >= 67) return spec.high;
  return spec.mid;
}

export function buildWhatYouCanCarry(L: Learner, slots: SlotState): LoadBar[] {
  return LOADS.map((spec) => {
    const value = Math.round((L[spec.key] as number) ?? 50);
    return {
      key: spec.key,
      label: spec.label,
      value,
      meaning: readLoad(spec, value),
      ifExceeded: spec.ifExceeded,
      lowConfidence: slots.lowConfidence[spec.key] ?? false,
    };
  });
}

// =========================================================================
// Section I-D — What pulls you (stated interests)
// =========================================================================

export interface PullItem {
  label: string;
  detail: string;
  families: string[];
}

export interface WhatPullsYou {
  passions: PullItem[];
  dream: PullItem | null;
  pulls: PullItem[];
  /** Family → how many of the learner's picks point at it. */
  familyStrength: { family: string; count: number }[];
  /** Honest statement of how much these moved the result. */
  weightNote: string;
  empty: boolean;
}

export function buildWhatPullsYou(part1: Part1Answers): WhatPullsYou {
  const passions = selectedPassions(part1.passion_ids).map((p) => ({
    label: p.label, detail: p.description, families: p.families,
  }));
  const dreamOpt = dreamById(part1.childhood_dream);
  const dream = dreamOpt
    ? { label: dreamOpt.label, detail: dreamOpt.hint ?? "What you said you wanted to be at ten.", families: dreamOpt.families }
    : null;
  const pulls = selectedPulls(part1.career_pull_ids).map((p) => ({
    label: p.label, detail: p.hint ?? "", families: p.families,
  }));

  const counts = new Map<string, number>();
  for (const item of [...passions, ...(dream ? [dream] : []), ...pulls]) {
    for (const f of item.families) counts.set(f, (counts.get(f) ?? 0) + 1);
  }

  const familyStrength = [...counts.entries()]
    .map(([family, count]) => ({ family, count }))
    .sort((a, b) => b.count - a.count || (a.family < b.family ? -1 : 1));

  return {
    passions, dream, pulls, familyStrength,
    weightNote:
      "These are the things you told us directly, so we treat them differently from the choices we watched you make. " +
      "They can lift work you said you cared about into view, and they reserve three places in your list for it — but they can never " +
      "promote a genuine misfit into your best matches. Twenty-two revealed decisions outrank three taps, every time.",
    empty: passions.length === 0 && !dream && pulls.length === 0,
  };
}

// =========================================================================
// Section II — Fitment by career family
// =========================================================================

export interface FamilyFitment {
  family: string;
  /** 0..100 — mean of the strongest few careers in this family. */
  fitment: number;
  /** How many eligible careers this family contributed. */
  eligible: number;
  /** Per-slot contribution, so the number is never unexplained. */
  breakdown: {
    howYouWork: number;   // archetype
    whatMatters: number;  // domain
    theDay: number;       // context
    whatYouNeed: number;  // values
    pay: number;          // salary
    outlook: number;      // demand
  };
  /** Best-scoring example titles from this family. */
  examples: { ciId: string; title: string }[];
  /** True when the learner named this family as a passion/pull. */
  named: boolean;
}

/** How many top careers per family feed the family score. */
const FAMILY_SAMPLE = 5;

/**
 * Score EVERY eligible career, then roll up per family.
 *
 * We take the mean of the best `FAMILY_SAMPLE` in each family rather than
 * the mean of all of them: a family with 400 careers would otherwise be
 * punished for its own breadth, and the learner only needs a handful of
 * doors in a family to be worth walking towards.
 */
export function buildFamilyFitment(L: Learner, careers: Career[]): FamilyFitment[] {
  const cap = effectiveCapacity(L);
  interface Row {
    ciId: string; title: string; total: number;
    arc: number; dom: number; ctx: number; val: number; sal: number; dem: number;
  }
  const byFamily = new Map<string, Row[]>();

  for (const c of careers) {
    if (entryCost(c) > cap) continue;
    const ctx = scoreContext(L, c).score;
    const val = scoreValues(L, c).score;
    const arc = scoreArchetype(L, c);
    const dom = scoreDomain(L, c);
    const sal = scoreSalary(L, c);
    const dem = scoreDemand(c);
    const total =
      arc * WEIGHTS.archetype + dom * WEIGHTS.domain + ctx * WEIGHTS.context +
      val * WEIGHTS.values + sal * WEIGHTS.salary + dem * WEIGHTS.demand;
    const arr = byFamily.get(c.family) ?? [];
    arr.push({ ciId: c.ciId, title: c.title, total, arc, dom, ctx, val, sal, dem });
    byFamily.set(c.family, arr);
  }

  const named = L.passionFamilies ?? new Set<string>();
  const out: FamilyFitment[] = [];

  for (const [family, rows] of byFamily) {
    rows.sort((a, b) => b.total - a.total || (a.title < b.title ? -1 : a.title > b.title ? 1 : 0));
    const sample = rows.slice(0, FAMILY_SAMPLE);
    const mean = (pick: (r: Row) => number) =>
      Math.round(sample.reduce((s, r) => s + pick(r), 0) / sample.length);
    out.push({
      family,
      fitment: mean((r) => r.total),
      eligible: rows.length,
      breakdown: {
        howYouWork: mean((r) => r.arc),
        whatMatters: mean((r) => r.dom),
        theDay: mean((r) => r.ctx),
        whatYouNeed: mean((r) => r.val),
        pay: mean((r) => r.sal),
        outlook: mean((r) => r.dem),
      },
      examples: sample.slice(0, 3).map((r) => ({ ciId: r.ciId, title: r.title })),
      named: named.has(family),
    });
  }

  out.sort((a, b) => b.fitment - a.fitment || (a.family < b.family ? -1 : 1));
  return out;
}

// =========================================================================
// Section III — Summary
// =========================================================================

export interface SummaryRow {
  ciId: string;
  title: string;
  family: string;
  tier: string;
  distance: string;
  fit: number;
  salary: string;
  /** Two reasons it matched and, when honest, one thing to watch. */
  fits: string[];
  watch: string | null;
  /** Axes for the fit-versus-reach plot. */
  reach: number;
  flags: string[];
}

const DISTANCE_REACH: Record<string, number> = {
  "Lateral move": 15,
  "Short reskill": 40,
  "Retrain 1-2 yrs": 70,
  "Full requalification": 95,
};

export function buildSummaryRows(results: MatchResult[]): SummaryRow[] {
  return results.map((r) => {
    const why = whyItMatched(r);
    const flags: string[] = [];
    if (r.market === "unpriced") flags.push("No verified pay data");
    if (r.marketStale) flags.push("Pay data over 24h old");
    if (r.confidence === "low") flags.push("Lower-confidence career record");
    if (r.passion > 0) flags.push("You said you're drawn to this");
    return {
      ciId: r.ciId,
      title: r.title,
      family: r.family,
      tier: r.tier,
      distance: r.distance,
      fit: Math.round(r.score),
      salary: formatSalary(r.salaryLo, r.salaryHi),
      fits: why.fits.slice(0, 2),
      watch: why.watch,
      reach: DISTANCE_REACH[r.distance] ?? 50,
      flags,
    };
  });
}

// =========================================================================
// Next steps
// =========================================================================

export interface NextStep {
  title: string;
  body: string;
}

export function buildNextSteps(band: Band, rows: SummaryRow[], part1: Part1Answers): NextStep[] {
  const first = rows[0];
  const steps: NextStep[] = [];

  if (band === "12to14") {
    return [
      { title: "Try one thing from a world that fits", body: "Not a career — a Saturday. A club, a video, a project, a person to ask. The point is to find out how it feels, not to decide anything." },
      { title: "Talk this report through with someone at home", body: "Read the archetype page together. Ask them whether it sounds like you. Their answer is data too." },
      { title: "Come back in a year", body: "You will change, and this report is meant to be re-run. Nothing here is a decision you've now made." },
    ];
  }

  if (first) {
    steps.push({
      title: `Read the full page on ${first.title}`,
      body: "Your report links straight to it: the actual day, the pay band, who thrives, how people get in, and what the work looks like in 2040. Read it before you fall in love with a job title.",
    });
  }
  steps.push({
    title: "Talk to two people already doing it",
    body: "One who loves it and one who left. Ask both what the worst week of the year looks like. Fifteen minutes each will tell you more than any report, including this one.",
  });

  if (band === "15to18") {
    steps.push({
      title: "Check the entry route against your stream",
      body: part1.stream_provisional
        ? "Your stream here is provisional — we inferred it from what school feels like, not from a decision you made. Confirm it before you plan around it."
        : "Map the qualifications your top matches ask for against the subjects you're taking. Where there's a gap, there's usually more than one route across it.",
    });
    steps.push({
      title: "Take one low-cost test of the work",
      body: "A short course, a shadowing day, a summer project. Evidence beats introspection, and it's cheap at your age.",
    });
  } else {
    steps.push({
      title: "Cost the cheapest route in",
      body: "Where a match asks for a qualification, we took the cheapest viable route — often a certificate rather than a degree. Price that route in time and money before you rule it out.",
    });
    steps.push({
      title: "Test one match inside 90 days",
      body: "A short project, a freelance piece, a conversation that leads somewhere. Small, reversible experiments are how careers actually change.",
    });
  }

  steps.push({
    title: "Re-run this in six months",
    body: "Your preferences move. This report is a snapshot with a date on it, not a verdict — and re-running it is free.",
  });

  return steps;
}

// =========================================================================
// Identity page — the one page a person would screenshot
// =========================================================================

/** The name of the artefact, in the learner's language. One place to change. */
export const REPORT_NAME = "WeKIT™ Career Clarity 360 Passport";
export const REPORT_NAME_SHORT = "Passport";

/** How a supporting archetype completes the sentence "a Builder who…". */
const PAIR_VERB: Record<Archetype, string> = {
  Builder: "builds",
  Thinker: "thinks it through",
  Creator: "makes things up",
  Connector: "brings people in",
  Organizer: "keeps it running",
  Leader: "takes the call",
  Caregiver: "looks after people",
  Advocate: "won't let it slide",
};

export interface IdentitySignal {
  label: string;
  value: number;
  meaning: string;
}

export interface IdentityPage {
  name: string;
  /** "A Builder who thinks it through" — the pair, said out loud. */
  pairLabel: string;
  /** One sentence a person would actually repeat about themselves. */
  sentence: string;
  /** Top-ranked cause, when they ranked one. */
  cause: string | null;
  causeLine: string | null;
  /** Exactly three signals, each with the sentence that explains it. */
  signals: IdentitySignal[];
  /** Safe to share: no scores, no pay, no ranked titles. */
  shareLine: string;
}

export function buildIdentityPage(args: {
  learnerName: string;
  band: Band;
  howYouWork: HowYouWork;
  fitment: FamilyFitment[];
  summary: SummaryRow[];
  part1: Part1Answers;
  convergenceCount: number;
}): IdentityPage {
  const { howYouWork: h, fitment, summary, part1 } = args;
  const topFamily = fitment[0] ?? null;
  const topCauseId = (part1.cause_rank_ids ?? [])[0];
  const cause = topCauseId ? causeById(topCauseId)?.label ?? null : null;

  const signals: IdentitySignal[] = [
    {
      label: `${h.primary} first`,
      value: h.rows[0]?.value ?? 100,
      meaning: `${h.primaryTagline}. This came from the choices you made, not from anything you said about yourself.`,
    },
    {
      label: h.rows[1] ? `${h.rows[1].name} underneath` : "One clear direction",
      value: h.rows[1]?.value ?? 0,
      meaning: h.rows[1]
        ? `${h.secondaryTagline}. It's the second gear you shift into when the first one isn't enough.`
        : "Your answers pointed in one direction almost every time, which is unusual and useful.",
    },
    topFamily
      ? {
        label: `Fits ${topFamily.family}`,
        value: topFamily.fitment,
        meaning: `Of every field of work we read, this one asks for most of what you already are — ${topFamily.eligible.toLocaleString("en-IN")} ${topFamily.eligible === 1 ? "role" : "roles"} inside it ${topFamily.eligible === 1 ? "is" : "are"} open to you right now.`,
      }
      : {
        label: "Still opening up",
        value: 0,
        meaning: "We didn't have enough open roles to name a field yet. Re-run this after you've tried a few things.",
      },
  ];

  const sentence = args.band === "12to14"
    ? `You're a ${h.primary}. ${h.primaryTagline}. That's not a job yet — it's the shape of the work you'd enjoy.`
    : `You're a ${h.primary} who ${PAIR_VERB[h.secondary]}${topFamily ? `, and the work that fits sits in ${topFamily.family}` : ""}.`;

  return {
    name: args.learnerName,
    pairLabel: `A ${h.primary} who ${PAIR_VERB[h.secondary]}`,
    sentence,
    cause,
    causeLine: cause
      ? `The thing you'd most want your work to fix: ${cause}.`
      : null,
    signals,
    shareLine: summary[0] && args.band !== "12to14"
      ? `${h.primary} · ${h.secondary} · ${summary.length} roles worth a real look`
      : `${h.primary} · ${h.secondary} · ${args.convergenceCount} places your interests overlap`,
  };
}

// =========================================================================
// For the people around you — the parent / mentor sheet
// =========================================================================

export interface ForTheirPeople {
  /** Who this page is addressed to, in their band's language. */
  addressee: string;
  intro: string;
  whatItSays: string[];
  whatItDoesNotSay: string[];
  /** Three questions worth asking this week. Specific to this report. */
  questions: string[];
  dontSay: { line: string; because: string };
  oneThing: string;
  /**
   * What an observer offered AND the learner approved, said back to them.
   *
   * Approved-only, on purpose: an observation the learner retracted is never
   * printed here, because a parent seeing their own rejected words in the
   * report would turn a consent mechanism into a betrayal. Absent entirely
   * when nobody was asked, or nothing was kept.
   */
  fromObserver?: {
    /** "What your mother added" — relationship, never a name. */
    title: string;
    /** Labels of the kept picks, in the learner's approved order. */
    kept: string[];
    /** How many of the observer's items the learner did not keep. */
    setAside: number;
    note: string;
  };
}

export function buildForTheirPeople(
  band: Band,
  h: HowYouWork,
  fitment: FamilyFitment[],
  summary: SummaryRow[],
  identity: IdentityPage,
  observer?: ApprovedObserverInput,
): ForTheirPeople {
  const first = summary[0] ?? null;
  const topFamily = fitment[0]?.family ?? null;
  const young = band === "12to14";


  const questions: string[] = [
    `"The report says you're mostly a ${h.primary} — ${h.primaryTagline.toLowerCase()}. Does that sound like you?" Their answer matters more than the page.`,
    topFamily
      ? `"What's the part of ${topFamily} that actually appeals — and what's the part you'd hate?" Wanting one half of a field is normal and worth naming.`
      : `"Which of these pages sounded most like you, and which didn't?" Disagreement is data.`,
    young
      ? `"What would you want to try this term — not decide, just try?" One Saturday's evidence beats a term of speculation.`
      : first
        ? `"Who do we know who does something like ${first.title}, and can we ask them what the worst week of the year looks like?"`
        : `"Who could we ask about the day-to-day of work like this?"`,
  ];

  return {
    addressee: young ? "Parents and teachers" : "The people you'd talk this over with",
    intro: young
      ? "This page is for the adult reading over their shoulder. They did the walk; the rest of this report belongs to them. This bit is yours."
      : "If you're going to show this to a parent, a mentor or a friend, hand them this page first. It saves the conversation from starting in the wrong place.",
    whatItSays: [
      `How they prefer to work: ${identity.pairLabel.toLowerCase()}.`,
      "The conditions they need from a working day, and how much a job can reasonably ask of them.",
      topFamily
        ? `Which fields of work fit that — ${topFamily} sits highest today.`
        : "Which directions fit that shape of person.",
      "Every number here arrives with the sentence that explains it. Where we couldn't verify something, we left it blank.",
    ],
    questions,
    whatItDoesNotSay: [
      "It is not an ability or aptitude test. There is no IQ, no marks, no ranking against other children.",
      "It is not a prediction, and it does not close any door. Entry routes here take the cheapest viable path, not the most prestigious one.",
      young
        ? "It does not name job titles, on purpose. At this age worlds of work are the honest unit."
        : "It is a snapshot with a date on it. Preferences move, and re-running it is free.",
    ],
    dontSay: {
      line: `"So you're going to be a ${first?.title ?? (topFamily ?? "professional")}, then?"`,
      because:
        "It turns a description into a decision, and the fastest way to lose a young person's trust in a report is to use it to close the conversation. Ask what they want to try next instead.",
    },
    oneThing: young
      ? "One thing that helps this week: help them try one small thing from a world that fits — a club, a video, a person to ask. Curiosity needs a door, not a plan."
      : "One thing that helps this week: offer an introduction, not an opinion. Fifteen minutes with someone doing the work is worth more than any report, including this one.",
    fromObserver: buildFromObserver(observer),
  };
}

/**
 * The approved half of a Second Opinion, said back to the person who gave it.
 *
 * Only ticked items appear. Retracted items are counted, never quoted — the
 * count is honest about the fact that the learner had the final say, without
 * putting rejected words in front of the person who wrote them.
 */
function buildFromObserver(
  observer?: ApprovedObserverInput,
): ForTheirPeople["fromObserver"] {
  if (!observer) return undefined;
  const label = (id: string) => PASSIONS.find((p) => p.id === id)?.label ?? null;
  const kept = [...(observer.passion_ids ?? []), ...(observer.cause_ids ?? [])]
    .map(label)
    .filter((l): l is string => Boolean(l));
  if (kept.length === 0) return undefined;
  const setAside =
    (observer.declined_passion_ids?.length ?? 0) + (observer.declined_cause_ids?.length ?? 0);
  const noun = relationshipNoun(observer.relationship);
  return {
    title: `What their ${noun} added — and they kept`,
    kept,
    setAside,
    note: setAside > 0
      ? `They read every observation and kept these. ${setAside} ${setAside === 1 ? "was" : "were"} set aside, which is their call to make and worth respecting rather than re-litigating.`
      : "They read every observation and kept all of them. These widened the range of work considered; they did not change who the report says they are.",
  };
}

// =========================================================================
// The whole dossier
// =========================================================================


export interface DossierInput {
  band: Band;
  part1: Part1Answers;
  slots?: SlotState;
  learner: Learner;
  tally: ArchetypeVector;
  results: MatchResult[];
  careers: Career[];
  totalConsidered: number;
  eligiblePool: number;
  ruledOutByStudy: number;
  studyRelaxed: boolean;
  learnerName?: string | null;
  generatedAt: Date;
  /** Sealed report hash when the canonical pipeline has landed. */
  reportHash?: string | null;
  attemptId?: string | null;
}

export interface Dossier {
  band: Band;
  bandLabel: string;
  learnerName: string;
  generatedOn: string;
  attemptRef: string;
  reportHash: string | null;
  headline: string;
  /** The one page a learner would screenshot. */
  identity: IdentityPage;
  /** The sheet addressed to a parent, mentor or teacher. */
  forTheirPeople: ForTheirPeople;
  howYouWork: HowYouWork;
  whatYouNeed: SpectrumGroup[];
  whatYouCanCarry: LoadBar[];
  whatPullsYou: WhatPullsYou;
  fitment: FamilyFitment[];
  summary: SummaryRow[];
  nextSteps: NextStep[];
  /** Convergence of Passions & Careers — the Venn model plus its audit trail. */
  convergence: Convergence;
  provenance: {
    totalConsidered: number;
    eligiblePool: number;
    ruledOutByStudy: number;
    studyRelaxed: boolean;
    lowConfidenceSlots: string[];
    /** Which fields we refused to invent. */
    notMeasured: string[];
    engine: string;
  };
  /** Whether pay, ranked titles and percentages may be shown at all. */
  showsPay: boolean;
  showsRankedTitles: boolean;
}

const BAND_LABEL: Record<Band, string> = {
  "12to14": "Explorers · ages 12–14",
  "15to18": "Pathfinders · ages 15–18",
  "18plus": "Career Clarity 360 · 18+",
};

/** Signals this walk deliberately does not measure. Stated, never implied. */
const NOT_MEASURED = [
  "Aptitude or ability. We did not administer a timed test, so this report contains no numerical, verbal, spatial or reasoning score. Anyone who gives you one from a preference questionnaire has invented it.",
  "Intelligence, personality disorder, or anything clinical. This is a fit assessment, not a diagnosis.",
  "Your grades. Where a route needs marks you don't have yet, that's a gap with a plan attached, not a closed door.",
];

export function buildDossier(input: DossierInput): Dossier {
  const {
    band, part1, learner, tally, results, careers, generatedAt,
  } = input;
  const slots = input.slots ?? emptySlots();
  const explorer = band === "12to14";

  const howYouWork = buildHowYouWork(tally, learner.archetypePrimary, learner.archetypeSecondary);
  const summary = explorer ? [] : buildSummaryRows(results);

  const lowConfidenceSlots = Object.keys(slots.lowConfidence);

  // Approved-only, read once through the same helper the engine used, so the
  // report cannot disagree with the score about what counted.
  const approvedObserver = part1.observer_input ?? readApprovedObserverInput() ?? undefined;

  const fitment = buildFamilyFitment(learner, careers);
  const convergence = buildConvergence({
    band, part1, slots, learner, results,
    eligiblePool: input.eligiblePool,
    ruledOutByStudy: input.ruledOutByStudy,
    studyRelaxed: input.studyRelaxed,
    // Approved-only, and read through the same helper the engine used, so
    // the report cannot disagree with the score about what counted.
    observer: approvedObserver,
    firstPersonStrength: firstPersonFamilyStrength(
      { band, part1, part2: { slots } } as never, band,
    ),
  });
  const learnerName = (input.learnerName ?? "").trim() || "Your Passport";
  const identity = buildIdentityPage({
    learnerName, band, howYouWork, fitment, summary, part1,
    convergenceCount: convergence.converged.length,
  });

  return {
    band,
    bandLabel: BAND_LABEL[band],
    learnerName,
    generatedOn: generatedAt.toLocaleDateString("en-IN", {
      day: "numeric", month: "long", year: "numeric",
    }),
    attemptRef: input.attemptId ? input.attemptId.slice(0, 8).toUpperCase() : "PREVIEW",
    reportHash: input.reportHash ?? null,
    headline: `You're ${withArticle(learner.archetypePrimary)}. ${sentenceCase(ARCHETYPE_TAGLINES[learner.archetypePrimary])}.`,
    identity,
    forTheirPeople: buildForTheirPeople(band, howYouWork, fitment, summary, identity, approvedObserver),
    howYouWork,
    whatYouNeed: buildWhatYouNeed(learner, slots),
    whatYouCanCarry: buildWhatYouCanCarry(learner, slots),
    whatPullsYou: buildWhatPullsYou(part1),
    convergence,
    fitment,
    summary,
    nextSteps: buildNextSteps(band, summary, part1),
    provenance: {
      totalConsidered: input.totalConsidered,
      eligiblePool: input.eligiblePool,
      ruledOutByStudy: input.ruledOutByStudy,
      studyRelaxed: input.studyRelaxed,
      lowConfidenceSlots,
      notMeasured: NOT_MEASURED,
      engine: "WCC360 v2 · client preview engine",
    },
    showsPay: !explorer,
    showsRankedTitles: !explorer,
  };
}

